import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import { linkifyText } from './linkify';
import {
  PassData,
  PassField,
  ApplePosterField,
  ApplePosterPreview,
  WalletFace,
  DevicePlatform,
  isStoreCardType,
  passTypeLabel,
  maskIfSensitiveCode,
  defaultFaceFor,
  autoContrastColor,
} from './types';

// The rendered pass. Three faces, each drawn the way the matching wallet
// draws it (and the way the intouch pass designer previews it, so what the
// merchant designed, what this page shows and what lands on the phone are
// the same picture):
//   classic  Apple Wallet on iOS 26 and earlier, and the iOS 27 fallback.
//   poster   Apple Wallet iOS 27 Poster face (full-bleed artwork).
//   google   Google Wallet card (Samsung is close enough to share it).
// Flipping shows the back: Apple's back fields / Google's details sheet.

const populated = (f?: PassField | null) => !!f && !!(f.label || f.value);

const auxRidesOnBack = (passType?: string) =>
  isStoreCardType(passType) || String(passType ?? '').toLowerCase() === 'generic';

const ContactlessIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" aria-hidden style={{ opacity: 0.7 }}>
    <path d="M6.5 8.5a8 8 0 010 7" />
    <path d="M10 6.5a11 11 0 010 11" />
    <path d="M13.5 4.5a14.5 14.5 0 010 15" />
  </svg>
);

// Shared barcode block. Honours use_barcode (a template with the barcode off
// must not preview one) and hide_sensitive_preview.
const BarcodeBlock: React.FC<{ passData: PassData; compact?: boolean }> = ({ passData, compact = false }) => {
  if (passData.use_barcode === false || !passData.pass_code) return null;
  const h = compact ? 56 : 64;
  return (
    <div className={`bg-white rounded-2xl px-4 py-3 flex flex-col items-center overflow-hidden ${compact ? '' : ''}`}>
      {passData.hide_sensitive_preview ? (
        <div className="h-14 flex items-center justify-center text-center text-xs text-gray-500 px-6">
          You'll activate your code once you add this to your wallet
        </div>
      ) : (!passData.barcode_type || passData.barcode_type === 'code128') ? (
        <Barcode value={passData.pass_code} format="CODE128" width={2} height={h} displayValue={false} margin={0} background="#ffffff" lineColor="#000000" />
      ) : passData.barcode_type === 'qr' ? (
        <QRCodeSVG value={passData.pass_code} size={compact ? 96 : 112} level="M" marginSize={0} bgColor="#ffffff" fgColor="#000000" />
      ) : (
        <div className="h-14 flex items-center justify-center text-[11px] uppercase tracking-widest text-gray-500 border border-dashed border-gray-300 rounded px-6">
          {passData.barcode_type} shown in wallet
        </div>
      )}
      {!passData.hide_sensitive_preview && (
        <p className="text-xs text-black mt-2 tracking-[0.2em] font-mono font-semibold">CODE {passData.pass_code}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Apple classic face
// ---------------------------------------------------------------------------
const PassCardFront: React.FC<{ passData: PassData }> = ({ passData }) => {
  const bg = passData.background_colour || '#1c1c1e';
  const fg = passData.primary_colour || '#ffffff';
  const muted = passData.secondary_colour || 'rgba(255,255,255,0.65)';
  const generic = String(passData.pass_type ?? '').toLowerCase() === 'generic';

  // Non-storeCard styles promote position_one to the large primary field;
  // the row below is positions two to four. storeCard keeps one to four in
  // a single row. Mirrors passApple.
  const usesPrimary = !isStoreCardType(passData.pass_type);
  const primary = usesPrimary && populated(passData.position_one)
    ? maskIfSensitiveCode(passData.position_one as PassField, passData)
    : null;
  const secondaries = (usesPrimary
    ? [passData.position_two, passData.position_three, passData.position_four]
    : [passData.position_one, passData.position_two, passData.position_three, passData.position_four]
  ).filter(populated).map((f) => maskIfSensitiveCode(f as PassField, passData));

  // Aux row only shows on the FRONT for eventTicket/coupon; storeCard and
  // generic push aux slots to the back. Mirrors passApple.
  const aux = auxRidesOnBack(passData.pass_type)
    ? []
    : ([passData.position_aux_one, passData.position_aux_two, passData.position_aux_three, passData.position_aux_four]
        .filter(populated) as PassField[]).map((f) => maskIfSensitiveCode(f, passData));

  return (
    <div className="rounded-3xl shadow-xl overflow-hidden w-full" style={{ backgroundColor: bg, color: fg }}>
      <div className="px-5 pt-4 pb-2 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {passData.logo_path && (
            <img src={passData.logo_path} alt={`${passData.organisation_name || 'Merchant'} logo`} className="h-9 max-w-[110px] object-contain rounded" />
          )}
          <ContactlessIcon color={fg} />
        </div>
        {populated(passData.position_header) && (
          <div className="text-right shrink-0">
            <p className="text-[10px] uppercase tracking-wider" style={{ color: muted }}>{passData.position_header!.label}</p>
            <p className="text-2xl font-semibold leading-tight">{maskIfSensitiveCode(passData.position_header as PassField, passData).value}</p>
          </div>
        )}
      </div>

      <div className="px-5 pb-2">
        <span className="inline-block text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-md bg-white/15 backdrop-blur-sm">
          {passTypeLabel(passData.pass_type)}
        </span>
      </div>

      {/* Generic shows a small thumbnail beside the primary field, not a
          strip. Every other type shows the strip image under the header. */}
      {generic ? (
        <div className="px-5 pt-2 pb-1 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            {primary && (
              <>
                <p className="text-[10px] uppercase tracking-wider mb-0.5 truncate" style={{ color: muted }}>{primary.label}</p>
                <p className="text-2xl font-semibold leading-tight truncate">{primary.value}</p>
              </>
            )}
          </div>
          {passData.banner_path && (
            <img src={passData.banner_path} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />
          )}
        </div>
      ) : (
        passData.banner_path && <img src={passData.banner_path} alt="" className="w-full h-auto object-contain block" />
      )}

      {((!generic && primary) || secondaries.length > 0 || aux.length > 0) && (
        <div className="px-5 pt-4 pb-1 space-y-3">
          {!generic && primary && (
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider mb-0.5 truncate" style={{ color: muted }}>{primary.label}</p>
              <p className="text-2xl font-semibold leading-tight truncate">{primary.value}</p>
            </div>
          )}
          {secondaries.length > 0 && (
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${secondaries.length}, minmax(0, 1fr))` }}>
              {secondaries.map((slot, i) => (
                <div key={`sec-${i}`} className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider mb-0.5 truncate" style={{ color: muted }}>{slot.label}</p>
                  <p className="text-sm font-medium truncate">{slot.value}</p>
                </div>
              ))}
            </div>
          )}
          {aux.length > 0 && (
            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${aux.length}, minmax(0, 1fr))` }}>
              {aux.map((slot, i) => (
                <div key={`aux-${i}`} className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wider mb-0.5 truncate" style={{ color: muted }}>{slot.label}</p>
                  <p className="text-sm font-medium truncate">{slot.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="px-5 pt-3 pb-5"><BarcodeBlock passData={passData} /></div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Apple iOS 27 Poster face, matched to a real iPhone (2026-10-13, keyword
// 0FXRY8Y): portrait 5:7, artwork full bleed under everything with a dark
// bottom gradient, logo + organisation top-left, header top-right, barcode in
// a white panel at ~62-78% of the height, primary label/value at ~84%/88%,
// footer at ~96%. Values are monospaced; labels keep their template case in
// the label colour (only the header label is uppercase). Every field and the
// barcode come from passData.apple_poster, which walletHandler builds with
// passApple's own builders, so this face shows exactly what the pass does.
// Positions are % of the card and type is cqw so the layout scales as one.
// ---------------------------------------------------------------------------
const POSTER_MONO = 'ui-monospace, "SF Mono", SFMono-Regular, Menlo, Consolas, monospace';

const PosterBarcode: React.FC<{ barcode: NonNullable<ApplePosterPreview['barcode']> }> = ({ barcode }) => {
  if (!barcode.message) {
    return (
      <div className="h-full flex items-center justify-center text-center text-gray-500 px-[4cqw]" style={{ fontSize: '2.8cqw' }}>
        You'll activate your code once you add this to your wallet
      </div>
    );
  }
  return (
    <div className="h-full flex flex-col items-center justify-center">
      <div className="w-full flex-1 min-h-0 flex items-center justify-center [&>svg]:max-w-full [&>svg]:h-full [&>svg]:w-auto">
        {barcode.format === 'qr' ? (
          <QRCodeSVG value={barcode.message} size={120} level="M" marginSize={0} bgColor="#ffffff" fgColor="#000000" />
        ) : barcode.format === 'code128' ? (
          <Barcode value={barcode.message} format="CODE128" width={2} height={60} displayValue={false} margin={0} background="#ffffff" lineColor="#000000" />
        ) : (
          <span className="uppercase tracking-widest text-gray-500" style={{ fontSize: '2.6cqw' }}>{barcode.format} shown in wallet</span>
        )}
      </div>
      {barcode.altText && (
        <p className="text-black mt-[0.8cqw] leading-none" style={{ fontFamily: POSTER_MONO, fontSize: '2.8cqw' }}>{barcode.altText}</p>
      )}
    </div>
  );
};

const PassCardPoster: React.FC<{ passData: PassData }> = ({ passData }) => {
  const poster = passData.apple_poster;
  // No poster block from the backend → we can't know what the pass shows on
  // this face, so draw the classic face rather than guess.
  if (!poster) return <PassCardFront passData={passData} />;

  const bg = passData.background_colour || '#1c1c1e';
  const fg = passData.primary_colour || '#ffffff';
  const accent = passData.secondary_colour || 'rgba(255,255,255,0.75)';
  const mask = (f?: ApplePosterField) => (f ? maskIfSensitiveCode(f, passData) : undefined);
  const header = mask(poster.headerFields[0]);
  const primary = mask(poster.primaryFields[0]);
  const footer = mask(poster.footerFields[0]);
  const logo = passData.poster_primary_logo_path || passData.logo_path;
  const at = (top: string): React.CSSProperties => ({ position: 'absolute', top, transform: 'translateY(-50%)' });

  return (
    <div
      className="relative w-full max-w-[380px] mx-auto rounded-3xl shadow-xl overflow-hidden"
      style={{
        aspectRatio: '5 / 7',
        containerType: 'inline-size',
        backgroundColor: bg,
        color: fg,
        ...(passData.poster_artwork_path
          ? { backgroundImage: `url("${passData.poster_artwork_path}")`, backgroundSize: 'cover', backgroundPosition: 'center' }
          : {}),
      } as React.CSSProperties}
    >
      <div className="absolute inset-0" style={{ background: 'linear-gradient(rgba(0,0,0,0) 45%, rgba(0,0,0,0.75) 100%)' }} />

      <div className="absolute flex items-center gap-[2.4cqw] min-w-0" style={{ top: '4%', left: '4%', maxWidth: '56%' }}>
        {logo && (
          <img src={logo} alt={`${passData.organisation_name || 'Merchant'} logo`} className="object-contain shrink-0" style={{ height: '9cqw', maxWidth: '22cqw', borderRadius: '1.6cqw' }} />
        )}
      </div>

      {header && (
        <div className="absolute text-right" style={{ top: '4%', right: '4%', maxWidth: '40%' }}>
          <p className="uppercase leading-tight truncate" style={{ color: accent, fontSize: '3.2cqw' }}>{header.label}</p>
          <p className="leading-tight truncate" style={{ fontFamily: POSTER_MONO, fontSize: '4.4cqw' }}>{header.value}</p>
        </div>
      )}

      {poster.barcode && (
        <div className="absolute bg-white overflow-hidden" style={{ top: '62%', height: '16%', left: '12%', right: '12%', borderRadius: '3cqw', padding: '2cqw 4cqw' }}>
          <PosterBarcode barcode={poster.barcode} />
        </div>
      )}

      {primary && (
        <>
          <p className="truncate leading-none" style={{ ...at('84%'), left: '4%', right: '4%', color: accent, fontFamily: POSTER_MONO, fontSize: '3.4cqw' }}>{primary.label}</p>
          <p className="truncate leading-none" style={{ ...at('88%'), left: '4%', right: '4%', fontFamily: POSTER_MONO, fontSize: '4.4cqw' }}>{primary.value}</p>
        </>
      )}

      {footer && footer.value && (
        <p className="truncate leading-none" style={{ ...at('96%'), left: '4%', right: '4%', fontFamily: POSTER_MONO, fontSize: '3.4cqw' }}>{footer.value}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Google Wallet face (Samsung shares it). Google draws its own chrome:
// circular logo + organisation, the header label / value as the card's
// header/subheader, fields as two-column rows, the barcode, then the hero
// image at the BOTTOM of the card. Text colour is black or white from the
// background; custom label/value colours are ignored, as on the real card.
// ---------------------------------------------------------------------------
const PassCardGoogle: React.FC<{ passData: PassData }> = ({ passData }) => {
  const bg = passData.background_colour || '#1c1c1e';
  const text = autoContrastColor(bg);
  const dim = text === '#ffffff' ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.6)';
  const header = passData.position_header ? maskIfSensitiveCode(passData.position_header as PassField, passData) : undefined;
  const front = [passData.position_one, passData.position_two, passData.position_three, passData.position_four]
    .filter(populated).map((f) => maskIfSensitiveCode(f as PassField, passData));

  return (
    <div className="rounded-3xl shadow-xl overflow-hidden w-full" style={{ backgroundColor: bg, color: text }}>
      <div className="px-5 pt-4 flex items-center gap-2.5 min-w-0">
        {passData.logo_path && (
          <img src={passData.logo_path} alt="" className="w-8 h-8 rounded-full object-cover bg-white shrink-0" />
        )}
        <p className="text-sm font-medium truncate">{passData.organisation_name || 'Pass'}</p>
      </div>
      {populated(header) && (
        <div className="px-5 pt-3">
          <p className="text-sm" style={{ color: dim }}>{header!.label}</p>
          <p className="text-2xl font-semibold leading-tight">{header!.value}</p>
        </div>
      )}
      {front.length > 0 && (
        <div className="px-5 pt-4 grid grid-cols-2 gap-x-4 gap-y-3">
          {front.map((slot, i) => (
            <div key={`g-${i}`} className="min-w-0">
              <p className="text-[11px]" style={{ color: dim }}>{slot.label}</p>
              <p className="text-sm font-medium truncate">{slot.value}</p>
            </div>
          ))}
        </div>
      )}
      <div className="px-5 pt-4"><BarcodeBlock passData={passData} compact /></div>
      {passData.banner_path && (
        <img src={passData.banner_path} alt="" className="w-full h-auto object-cover block mt-4" />
      )}
      {!passData.banner_path && <div className="h-4" />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Back of pass (Apple) / details sheet (Google). On the poster face Fields 3
// and 4 have no front slot, so they lead the back, matching the real pass.
// ---------------------------------------------------------------------------
const PassCardBack: React.FC<{ passData: PassData; face: WalletFace }> = ({ passData, face }) => {
  const google = face === 'google';
  const bg = google ? '#ffffff' : (passData.background_colour || '#1c1c1e');
  const fg = google ? '#202124' : (passData.primary_colour || '#ffffff');
  const muted = google ? '#5f6368' : (passData.secondary_colour || 'rgba(255,255,255,0.65)');
  const tags = passData.tags ?? [];

  const posterOverflow = face === 'poster'
    ? ([passData.position_three, passData.position_four].filter(populated) as PassField[])
    : [];
  const auxAsBack = (google || auxRidesOnBack(passData.pass_type))
    ? ([passData.position_aux_one, passData.position_aux_two, passData.position_aux_three, passData.position_aux_four].filter(populated) as PassField[])
    : [];
  const rows = [...posterOverflow, ...auxAsBack, ...tags].map((f) => maskIfSensitiveCode(f, passData));
  // Action links on the Apple back: plain tappable text (the link's label),
  // no icon, no URL.
  const actions = (passData.walletActions ?? []);
  // Both Apple faces carry every action as a back link (the pkpass's
  // posterGeneric backFields include the featured ones too).
  const backLinks = google ? [] : actions;

  return (
    <div className={`rounded-3xl shadow-xl overflow-hidden w-full ${google ? 'border border-gray-200' : ''}`} style={{ backgroundColor: bg, color: fg }}>
      <div className={`px-5 pt-5 pb-3 flex items-center justify-between gap-3 border-b ${google ? 'border-gray-200' : 'border-white/10'}`}>
        <div className="flex items-center gap-2.5 min-w-0">
          {passData.logo_path && <img src={passData.logo_path} alt="" className="h-8 max-w-[100px] object-contain rounded" />}
          <p className="text-base font-semibold truncate">{passData.organisation_name || 'Pass details'}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-md shrink-0 ${google ? 'bg-gray-100 text-gray-600' : 'bg-white/15'}`}>
          {google ? 'Details' : 'Back of pass'}
        </span>
      </div>
      <div className="px-5 py-4">
        {rows.length > 0 || backLinks.length > 0 ? (
          <div className="space-y-3.5">
            {rows.map((field, idx) => (
              <div key={`${field.label}-${idx}`} className={`pb-3.5 border-b last:border-b-0 last:pb-0 ${google ? 'border-gray-100' : 'border-white/10'}`}>
                <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: muted }}>{field.label}</p>
                <p className="text-sm font-medium whitespace-pre-line break-words leading-relaxed">{linkifyText(field.value)}</p>
              </div>
            ))}
            {backLinks.map((a) => (
              <div key={`link-${a.id}`} className="pb-3.5 border-b border-white/10 last:border-b-0 last:pb-0">
                <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium" style={{ color: '#5ac8fa' }} onClick={(e) => e.stopPropagation()}>
                  {a.label}
                </a>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-center py-8" style={{ color: muted }}>No extra details on the back of this pass.</p>
        )}
      </div>
    </div>
  );
};

// Flip affordance ABOVE the card: white three-dots circle (mirrors Apple
// Wallet's real "…" gesture) followed by "Flip to view back".
const FlipCTA: React.FC<{ isFlipped: boolean; onFlip: () => void; google: boolean }> = ({ isFlipped, onFlip, google }) => (
  <button type="button" onClick={onFlip} className="flex items-center gap-2.5" aria-label={isFlipped ? 'Show front' : (google ? 'Show details' : 'Flip to view back')}>
    <span className="relative w-12 h-12 bg-white text-[#06251B] rounded-full shadow-md flex items-center justify-center hover:shadow-lg transition-shadow shrink-0">
      {!isFlipped && (
        <motion.span
          className="absolute inset-0 rounded-full border border-[#06251B] pointer-events-none"
          animate={{ scale: [1, 1.12], opacity: [0.15, 0] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeOut' }}
          aria-hidden
        />
      )}
      {isFlipped ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
          <circle cx="6" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="18" cy="12" r="2" />
        </svg>
      )}
    </span>
    <span className="flex items-center gap-1.5 text-sm font-bold tracking-wide text-[#06251B] select-none whitespace-nowrap">
      <svg className="w-4 h-4 text-[#0A3B2C]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 12a9 9 0 11-2.6-6.4" /><path d="M21 3v6h-6" />
      </svg>
      {isFlipped ? 'Show front' : (google ? 'Show details' : 'Flip to view back')}
    </span>
  </button>
);

const FaceSwitch: React.FC<{ options: Array<{ value: WalletFace; label: string }>; value: WalletFace; onChange: (f: WalletFace) => void }> = ({ options, value, onChange }) => (
  <div className="flex items-center gap-1 text-xs font-semibold bg-[#F5F8F1] rounded-full p-1">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        onClick={() => onChange(o.value)}
        className={`px-3 py-1 rounded-full transition ${value === o.value ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const PassCard: React.FC<{
  passData: PassData;
  isFlipped: boolean;
  onFlip: () => void;
  platform?: DevicePlatform;
  iosMajor?: number | null;
  onFaceChange?: (face: WalletFace) => void;
}> = ({ passData, isFlipped, onFlip, platform = 'desktop', iosMajor = null, onFaceChange }) => {
  // Start on the face THIS device's wallet will show. The switcher only
  // offers faces that make sense for the device: an iPhone can compare
  // poster vs classic, a desktop can also see the Google card, an Android
  // phone just sees its card.
  const [face, setFace] = React.useState<WalletFace>(() => defaultFaceFor(platform, iosMajor, passData));
  React.useEffect(() => { onFaceChange?.(face); }, [face, onFaceChange]);

  const posterOn = !!passData.poster_generic_enabled;
  const options: Array<{ value: WalletFace; label: string }> = [];
  if (platform !== 'android') {
    if (posterOn) options.push({ value: 'poster', label: 'iOS 27' });
    options.push({ value: 'classic', label: posterOn ? 'iOS 26' : 'Apple' });
  }
  if (platform === 'desktop') options.push({ value: 'google', label: 'Android' });
  const showSwitcher = options.length > 1;

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3 pl-1 pr-2">
        {showSwitcher ? <FaceSwitch options={options} value={face} onChange={setFace} /> : <div />}
        <FlipCTA isFlipped={isFlipped} onFlip={onFlip} google={face === 'google'} />
      </div>

      <div style={{ perspective: 1200 }}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isFlipped ? `back-${face}` : face}
            initial={{ rotateY: isFlipped ? 90 : -90, opacity: 0.4 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: isFlipped ? -90 : 90, opacity: 0.4 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            onClick={onFlip}
            className="cursor-pointer"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {isFlipped
              ? <PassCardBack passData={passData} face={face} />
              : face === 'poster'
                ? <PassCardPoster passData={passData} />
                : face === 'google'
                  ? <PassCardGoogle passData={passData} />
                  : <PassCardFront passData={passData} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PassCard;
